import {
  ArticleInterface,
  CommentInterface
} from '@interfaces/MarketNewsInterface';
import BaseApiClient from '../BaseApiClient';
import { ListDataParams, ListDataResponse } from '../types';

export class ApiMarketNews extends BaseApiClient {
  /**
   * createArticle
   */
  async createArticle(payload: ArticleInterface): Promise<ArticleInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post('/article', payload)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * updateArticle
   */
  async updateArticle(
    id: string,
    payload: ArticleInterface
  ): Promise<ArticleInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/article/${id}`, payload)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * listArticles
   */
  async listArticles(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<ArticleInterface>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.title = searchTerm;
      }
      this.client
        .getList('/article', listData)
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta.current_page,
                perPage: res.data.meta.per_page,
                total: res.data.meta.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * getArticle
   */
  async getArticle(id: string): Promise<ArticleInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get(
          `/article/${id}?include[]=user&include[]=user.roles&include[]=articlePublisher`
        )
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * getComments
   */
  async getComments(
    id: string,
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<CommentInterface>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.q = searchTerm;
      }
      this.client
        .getList(`/article/${id}/article-comment?page[size]=10&page[number]=1`)
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta.current_page,
                perPage: res.data.meta.per_page,
                total: res.data.meta.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * addArticleComment
   */
  async addArticleComment(
    id: string,
    payload: { content: string }
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/article/${id}/article-comment`, payload)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * favouriteArticle
   */
  async favouriteArticle(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/article/${id}/article-favorite`)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * contactAuthor
   */
  async contactAuthor(
    id: string,
    payload: {
      subject: string;
      content: string;
    }
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/article/${id}/article-contact-author`, payload)
        .then((res) => {
          if (res) {
            resolve(res);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * shareArticle
   */
  async shareArticle(id: string, payload: { user_id: string }): Promise<any> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`/article/${id}/article-share`, payload)
        .then((res) => {
          if (res) {
            resolve(res);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * uploadImage
   */
  async uploadImage(payload: { image: File | null }): Promise<any> {
    return new Promise((resolve, reject) => {
      // const formData = new FormData();
      this.client
        .post('/article/upload-image', payload, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * deleteArticle
   */
  async deleteArticle(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.client
        .delete(`/article/${id}`)
        .then((res) => {
          if (res) {
            resolve(res);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
}

export { marketNewsQueryKeys } from './query-keys';
export type {
  MarketNewsArticlesListQueryParams,
  MarketNewsArticleCommentsQueryParams
} from './query-keys';
export {
  useMarketNewsArticlesListQuery,
  useMarketNewsArticleQuery,
  useMarketNewsArticleCommentsQuery
} from './use-market-news-queries';
export {
  useMarketNewsCreateArticleMutation,
  useMarketNewsUpdateArticleMutation,
  useMarketNewsAddArticleCommentMutation,
  useMarketNewsFavouriteArticleMutation,
  useMarketNewsContactAuthorMutation,
  useMarketNewsShareArticleMutation,
  useMarketNewsUploadImageMutation,
  useMarketNewsDeleteArticleMutation
} from './use-market-news-mutations';
export type {
  MarketNewsUpdateArticleVariables,
  MarketNewsAddCommentVariables,
  MarketNewsContactAuthorVariables,
  MarketNewsShareArticleVariables
} from './use-market-news-mutations';
